import type {
  DealerInvoiceFilterInput,
  InvoicesGlobalSearchQuery,
  InvoicesGlobalSearchQueryVariables,
} from "@/gql/graphql";
import { INVOICES_GLOBAL_SEARCH_QUERY } from "@/components/Navigation/GlobalHeader/GlobalSearch/global-search-queries";
import type {
  GlobalSearchApolloClient,
  InvoiceAutocompleteOptions,
} from "@/components/Navigation/GlobalHeader/GlobalSearch/types";
import {
  GLOBAL_SEARCH_PAGINATION_DEFAULTS,
  executeGlobalSearchQuery,
} from "./executeGlobalSearchQuery";

function buildGlobalSearchInvoicesWhere(
  searchTerm: string
): DealerInvoiceFilterInput | undefined {
  const trimmedSearchTerm = searchTerm.trim();
  if (!trimmedSearchTerm) {
    return undefined;
  }
  return {
    or: [
      { contract: { contractNumberSearch: { contains: trimmedSearchTerm } } },
      { contract: { purchaseOrderNumber: { contains: trimmedSearchTerm } } },
      { contract: { customerName: { contains: trimmedSearchTerm } } },
    ],
  };
}

export async function fetchInvoices(
  client: GlobalSearchApolloClient,
  accountId: string,
  searchTerm: string,
  signal?: AbortSignal
): Promise<{ totalCount: number; items: InvoiceAutocompleteOptions[] }> {
  const where = buildGlobalSearchInvoicesWhere(searchTerm);
  if (!where) {
    return { totalCount: 0, items: [] };
  }
  const data = await executeGlobalSearchQuery<
    InvoicesGlobalSearchQuery,
    InvoicesGlobalSearchQueryVariables
  >({
    client,
    query: INVOICES_GLOBAL_SEARCH_QUERY,
    variables: {
      ...GLOBAL_SEARCH_PAGINATION_DEFAULTS,
      dealerAccountId: accountId,
      where,
    },
    signal,
  });

  const invoices = data?.dealerInvoices?.nodes ?? [];

  return {
    totalCount: data?.dealerInvoices?.totalCount ?? 0,
    items: invoices.map((node) => ({
      invoiceId: node.id,
      contractNumber: node.contract?.contractNumberSearch ?? "",
      customerName: node.contract?.customerName ?? "",
      purchaseOrderNumber: node.contract?.purchaseOrderNumber ?? "",
      createdDate: node.createdDate,
    })),
  };
}
